import {
    ADDRESS_VALIDATE_UNAVAILABLE_MESSAGE,
    type AddressSuggestionOverlayMode,
} from '~~/layers/base/utils/crane/addressVerifyPolicy'

export type { AddressSuggestionOverlayMode } from '~~/layers/base/utils/crane/addressVerifyPolicy'

/**
 * Avalara suggestion picker copy for `CraneAddressForm` save (profile create/edit).
 * AdaptiveDrawer `title` is the aria string; the visible heading uses lead + em.
 */
export const addressSuggestionCopy = {
    eyebrow: 'Address verification',
    titleLead: 'Confirm the ',
    titleEmphasis: 'address',
    ariaTitle: 'Confirm the address',
    blockTitleLead: 'Undeliverable ',
    blockTitleEmphasis: 'address',
    blockAriaTitle: 'Undeliverable address',
    subtitle:
        "This address couldn't be verified exactly as typed. Save it as written, or use the verified suggestion.",
    blockSubtitle:
        'The address you have entered cannot be delivered to and no suggested address could be found.',
    suggestedPill: 'Suggested',
    manualPill: 'Manual',
    notValidPill: 'Not valid',
    noSuggestionAlertTitle: 'No Suggested Address Available',
    noSuggestionAlertDesc: 'No verified match could be found for the address you entered.',
    footerSuggestionOnly:
        'The address you have entered is not valid, use the suggested address or enter a different address.',
    footerBlock: 'Edit the address or enter a different one to continue.',
    editAddressLabel: 'Edit Address',
    closeAriaLabel: 'Close',
    /** Shown when validate cannot proceed after the picker. */
    invalidMessage: 'Address could not be verified. Please check the fields above.',
    /** Shown when Avalara is unreachable (save blocked). */
    unavailableMessage: ADDRESS_VALIDATE_UNAVAILABLE_MESSAGE,
} as const

/** Per-mode drawer head + body chrome (MVP `HMAddressVerifyDrawer`). */
export type AddressSuggestionDrawerHead = {
    ariaTitle: string
    titleLead: string
    titleEmphasis: string
    subtitle: string
}

export type AddressSuggestionDrawerChrome = AddressSuggestionDrawerHead & {
    showAlert: boolean
    alertVariant: 'info' | 'error'
    alertTitle: string
    alertBody: string
    showSuggestedRow: boolean
    showManualRow: boolean
    /** Manual row is tappable to confirm save (confirm mode). */
    manualSelectable: boolean
    manualPill: 'manual' | 'not-valid'
    manualMuted: boolean
    footer: string | null
}

const confirmHead: AddressSuggestionDrawerHead = {
    ariaTitle: addressSuggestionCopy.ariaTitle,
    titleLead: addressSuggestionCopy.titleLead,
    titleEmphasis: addressSuggestionCopy.titleEmphasis,
    subtitle: addressSuggestionCopy.subtitle,
}

const blockHead: AddressSuggestionDrawerHead = {
    ariaTitle: addressSuggestionCopy.blockAriaTitle,
    titleLead: addressSuggestionCopy.blockTitleLead,
    titleEmphasis: addressSuggestionCopy.blockTitleEmphasis,
    subtitle: addressSuggestionCopy.blockSubtitle,
}

const noAlert = {
    showAlert: false,
    alertVariant: 'error' as const,
    alertTitle: '',
    alertBody: '',
}

/** Verify drawer layout + copy keyed by overlay mode. */
export const addressSuggestionDrawerChrome: Record<
    AddressSuggestionOverlayMode,
    AddressSuggestionDrawerChrome
> = {
    confirm: {
        ...confirmHead,
        ...noAlert,
        showSuggestedRow: true,
        showManualRow: true,
        manualSelectable: true,
        manualPill: 'manual',
        manualMuted: false,
        footer: null,
    },
    suggest_only: {
        ...confirmHead,
        ...noAlert,
        showSuggestedRow: true,
        showManualRow: true,
        manualSelectable: false,
        manualPill: 'not-valid',
        manualMuted: true,
        footer: addressSuggestionCopy.footerSuggestionOnly,
    },
    block: {
        ...blockHead,
        showAlert: true,
        alertVariant: 'error',
        alertTitle: addressSuggestionCopy.noSuggestionAlertTitle,
        alertBody: addressSuggestionCopy.noSuggestionAlertDesc,
        showSuggestedRow: false,
        showManualRow: true,
        manualSelectable: false,
        manualPill: 'not-valid',
        manualMuted: true,
        footer: addressSuggestionCopy.footerBlock,
    },
}
